// MF-67 — Create/Edit a Journal Severity: one step of the school's severity scale (e.g. High). The order
// of the scale is set by dragging in the list. Severity only applies to records whose type uses it.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Box, Button, CircularProgress, TextField } from '@mui/material'
import { configPath } from '../paths'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import ColorField from '../components/ColorField'
import ColorBadge from '../components/ColorBadge'
import { inputSx, twoColumnSx } from '../components/formStyles'
import { DEFAULT_COLOR, isHex } from '../components/colorTokens'
import {
  getSeverity,
  getSeverities,
  createSeverity,
  updateSeverity
} from '../../../../services/journal/severities.service'

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const SeverityForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [label, setLabel] = useState('')
  // (the code is derived from the label on create and never shown)
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [colorOk, setColorOk] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadingData, setLoadingData] = useState(Boolean(id))

  useEffect(() => {
    if (!id) return
    getSeverity(id)
      .then(({ data }) => {
        if (!data) return
        setLabel(data.label || '')
        setDescription(data.description || '')
        setColor(isHex(data.color) ? data.color.toUpperCase() : DEFAULT_COLOR)
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  const onColorValid = useCallback((ok) => setColorOk(ok), [])
  const canSave = label.trim() && colorOk && !saving

  const onSave = async () => {
    setSaving(true)
    try {
      const payload = { label: label.trim(), description: description.trim(), color }
      if (id) {
        await updateSeverity(id, payload)
      } else {
        const { data } = await getSeverities({ limit: 1 })
        await createSeverity({ ...payload, code: slug(label), order: data?.totalRecords ?? 0 })
      }
      enqueueSnackbar('Severity saved', { variant: 'success' })
      navigate(configPath('severities'))
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save severity', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  if (loadingData) {
    return (
      <div className='flex justify-center' style={{ padding: '40px 0' }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <Box
      className='grid items-start'
      style={{ gap: 24, padding: '24px 8px', maxWidth: 1040, margin: '0 auto' }}
      sx={twoColumnSx}
    >
      <FormCard
        title={id ? 'Severity details' : 'New severity'}
        description='One step of the severity scale (how serious a concern is). Templates of types that use severity pick one; entries inherit it.'
        footer={
          <>
            <Button color='inherit' onClick={() => navigate(configPath('severities'))}>
              Cancel
            </Button>
            <Button variant='contained' disabled={!canSave} onClick={onSave}>
              {saving ? 'Saving…' : 'Save severity'}
            </Button>
          </>
        }
      >
        <FormField
          label='Name'
          required
          htmlFor='severity-label'
          hint='e.g. Minor, Moderate, Significant, Major, Critical'
        >
          <TextField
            id='severity-label'
            size='small'
            fullWidth
            autoFocus={!id}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={inputSx}
          />
        </FormField>
        <FormField
          label='Description'
          htmlFor='severity-description'
          hint='Explain when to use it. Shown when staff pick a severity.'
        >
          <TextField
            id='severity-description'
            size='small'
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='e.g. Needs a follow-up'
            sx={inputSx}
          />
        </FormField>
        <ColorField value={color} onChange={setColor} onValidChange={onColorValid} />
      </FormCard>

      <FormCard title='Preview' style={{ position: 'sticky', top: 16 }}>
        <div
          className='flex flex-col items-center'
          style={{ gap: 10, padding: '28px 16px', borderRadius: 10, background: 'rgb(248, 250, 252)' }}
        >
          <ColorBadge label={label.trim() || 'Severity name'} color={color} size='lg' />
          {description.trim() ? (
            <span style={{ fontSize: 13, color: 'rgb(71, 85, 105)', textAlign: 'center' }}>
              {description.trim()}
            </span>
          ) : null}
        </div>
      </FormCard>
    </Box>
  )
}

export default SeverityForm
